import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getUsers, updateUserRole, uploadAdminBook, getAdminBook, getAuditLogs } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { 
  Users, 
  BookOpen, 
  FileText,
  Shield,
  Save,
  RefreshCw,
  Activity,
  AlertTriangle,
  Plus,
  Trash2,
  Edit,
  ShieldCheck,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';

const AdminPage = () => {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [safetyBook, setSafetyBook] = useState('');
  const [unionBook, setUnionBook] = useState('');
  const [savingBook, setSavingBook] = useState(false);
  
  // Event Free Performance Tool Book state
  const [efpEntries, setEfpEntries] = useState([]);
  const [efpDialogOpen, setEfpDialogOpen] = useState(false);
  const [editingEfpEntry, setEditingEfpEntry] = useState(null);
  const [efpForm, setEfpForm] = useState({
    id: '',
    hazard_category: '',
    hazard_name: '',
    hazard_description: '',
    mitigation_strategies: [''],
    applicable_work_types: [],
    severity: 'medium',
    ppe_required: [],
    additional_notes: ''
  });

  // Hazard categories for dropdown
  const hazardCategories = [
    'Electrical',
    'Fall Protection',
    'Confined Space',
    'Arc Flash',
    'Weather Related',
    'Heavy Equipment',
    'Chemical/Hazmat',
    'Ergonomic',
    'Excavation/Trenching',
    'Fire/Explosion',
    'Pressurized Systems',
    'Rotating Equipment',
    'Other'
  ];

  // Work types for multi-select
  const workTypes = [
    'Transformer Work',
    'Breaker Work',
    'Relay/Protection',
    'Switching Operations',
    'Battery/DC Systems',
    'Cable Installation',
    'Bus Work',
    'Grounding',
    'Testing',
    'Maintenance',
    'Installation',
    'Inspection',
    'Emergency Response'
  ];

  // PPE options
  const ppeOptions = [
    'Hard Hat',
    'Safety Glasses',
    'Arc-rated Face Shield',
    'FR Clothing (Base)',
    'FR Clothing (40+ cal)',
    'Rubber Gloves',
    'Rubber Sleeves',
    'Safety Boots',
    'Fall Harness',
    'Hearing Protection',
    'Respirator',
    'Chemical Gloves',
    'High-Visibility Vest'
  ];

  useEffect(() => {
    if (!isAdmin()) {
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, safetyRes, unionRes, efpRes, auditRes] = await Promise.all([
        getUsers(),
        getAdminBook('safety_rule_book').catch(() => ({ data: { content: '' } })),
        getAdminBook('union_book').catch(() => ({ data: { content: '' } })),
        getAdminBook('event_free_performance_book').catch(() => ({ data: { content: '' } })),
        getAuditLogs({ limit: 50 }).catch(() => ({ data: [] }))
      ]);
      setUsers(usersRes.data);
      setSafetyBook(safetyRes.data?.content || '');
      setUnionBook(unionRes.data?.content || '');
      
      // Parse EFP entries from stored JSON
      try {
        const efpContent = efpRes.data?.content || '[]';
        const parsedEntries = JSON.parse(efpContent);
        setEfpEntries(Array.isArray(parsedEntries) ? parsedEntries : []);
      } catch {
        setEfpEntries([]);
      }
      
      setAuditLogs(auditRes.data || []);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await updateUserRole(userId, newRole);
      toast.success('User role updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleSaveSafetyBook = async () => {
    setSavingBook(true);
    try {
      await uploadAdminBook('safety_rule_book', safetyBook);
      toast.success('Safety Rule Book updated');
    } catch (error) {
      toast.error('Failed to update Safety Rule Book');
    } finally {
      setSavingBook(false);
    }
  };

  const handleSaveUnionBook = async () => {
    setSavingBook(true);
    try {
      await uploadAdminBook('union_book', unionBook);
      toast.success('Union Book updated');
    } catch (error) {
      toast.error('Failed to update Union Book');
    } finally {
      setSavingBook(false);
    }
  };

  // EFP Entry handlers
  const resetEfpForm = () => {
    setEfpForm({
      id: '',
      hazard_category: '',
      hazard_name: '',
      hazard_description: '',
      mitigation_strategies: [''],
      applicable_work_types: [],
      severity: 'medium',
      ppe_required: [],
      additional_notes: ''
    });
    setEditingEfpEntry(null);
  };

  const handleOpenEfpDialog = (entry = null) => {
    if (entry) {
      setEditingEfpEntry(entry);
      setEfpForm({
        ...entry,
        mitigation_strategies: entry.mitigation_strategies?.length > 0 ? entry.mitigation_strategies : ['']
      });
    } else {
      resetEfpForm();
    }
    setEfpDialogOpen(true);
  };

  const handleAddMitigationStrategy = () => {
    setEfpForm(prev => ({
      ...prev,
      mitigation_strategies: [...prev.mitigation_strategies, '']
    }));
  };

  const handleRemoveMitigationStrategy = (index) => {
    setEfpForm(prev => ({
      ...prev,
      mitigation_strategies: prev.mitigation_strategies.filter((_, i) => i !== index)
    }));
  };

  const handleMitigationChange = (index, value) => {
    setEfpForm(prev => ({
      ...prev,
      mitigation_strategies: prev.mitigation_strategies.map((m, i) => i === index ? value : m)
    }));
  };

  const toggleWorkType = (workType) => {
    setEfpForm(prev => ({
      ...prev,
      applicable_work_types: prev.applicable_work_types.includes(workType)
        ? prev.applicable_work_types.filter(t => t !== workType)
        : [...prev.applicable_work_types, workType]
    }));
  };

  const togglePpe = (ppe) => {
    setEfpForm(prev => ({
      ...prev,
      ppe_required: prev.ppe_required.includes(ppe)
        ? prev.ppe_required.filter(p => p !== ppe)
        : [...prev.ppe_required, ppe]
    }));
  };

  const handleSaveEfpEntry = async () => {
    if (!efpForm.hazard_category || !efpForm.hazard_name || !efpForm.hazard_description) {
      toast.error('Please fill in all required fields');
      return;
    }

    const filteredMitigations = efpForm.mitigation_strategies.filter(m => m.trim() !== '');
    if (filteredMitigations.length === 0) {
      toast.error('Please add at least one mitigation strategy');
      return;
    }

    setSavingBook(true);
    try {
      let updatedEntries;
      const entryData = {
        ...efpForm,
        mitigation_strategies: filteredMitigations,
        id: efpForm.id || `efp_${Date.now()}`
      };

      if (editingEfpEntry) {
        updatedEntries = efpEntries.map(e => e.id === editingEfpEntry.id ? entryData : e);
      } else {
        updatedEntries = [...efpEntries, entryData];
      }

      // Save as JSON to the admin_books collection
      await uploadAdminBook('event_free_performance_book', JSON.stringify(updatedEntries, null, 2));
      setEfpEntries(updatedEntries);
      setEfpDialogOpen(false);
      resetEfpForm();
      toast.success(editingEfpEntry ? 'Hazard entry updated' : 'Hazard entry added');
    } catch (error) {
      toast.error('Failed to save hazard entry');
    } finally {
      setSavingBook(false);
    }
  };

  const handleDeleteEfpEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this hazard entry?')) return;
    
    setSavingBook(true);
    try {
      const updatedEntries = efpEntries.filter(e => e.id !== entryId);
      await uploadAdminBook('event_free_performance_book', JSON.stringify(updatedEntries, null, 2));
      setEfpEntries(updatedEntries);
      toast.success('Hazard entry deleted');
    } catch (error) {
      toast.error('Failed to delete hazard entry');
    } finally {
      setSavingBook(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low': return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-slate-100 text-slate-800 border-slate-300';
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'supervisor': return 'bg-purple-100 text-purple-800';
      case 'crew_leader': return 'bg-blue-100 text-blue-800';
      case 'crew_member': return 'bg-green-100 text-green-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  if (!isAdmin()) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <Shield className="w-16 h-16 mx-auto mb-4 text-red-300" />
          <h3 className="text-lg font-semibold mb-2">Access Denied</h3>
          <p className="text-slate-500">You do not have permission to access this page.</p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#0056D2]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="admin-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Admin Panel
        </h1>
        <p className="text-slate-600">Manage users, permissions, and system settings</p>
      </div>

      <Tabs defaultValue="users" className="space-y-6">
        <TabsList>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Users
          </TabsTrigger>
          <TabsTrigger value="books" className="flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            Safety & Union Books
          </TabsTrigger>
          <TabsTrigger value="efp" className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Event Free Performance
          </TabsTrigger>
          <TabsTrigger value="audit" className="flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Audit Logs
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                User Management
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {users.map((user) => (
                  <div 
                    key={user.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                    data-testid={`user-${user.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-[#0056D2] rounded-full flex items-center justify-center text-white font-bold">
                        {user.full_name?.charAt(0) || 'U'}
                      </div>
                      <div>
                        <p className="font-semibold">{user.full_name}</p>
                        <p className="text-sm text-slate-500">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge className={getRoleBadgeColor(user.role)}>
                        {user.role?.replace('_', ' ')}
                      </Badge>
                      <Select 
                        value={user.role} 
                        onValueChange={(v) => handleRoleChange(user.id, v)}
                      >
                        <SelectTrigger className="w-36">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="supervisor">Supervisor</SelectItem>
                          <SelectItem value="crew_leader">Crew Leader</SelectItem>
                          <SelectItem value="crew_member">Crew Member</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Books Tab */}
        <TabsContent value="books">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Safety Rule Book */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-red-500" />
                  Safety Rule Book
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-sm text-slate-500">
                    This content is used by Ask Stewie to answer safety-related questions.
                    Include section numbers and page references for accurate citations.
                  </p>
                  <Textarea
                    placeholder="Paste or type the Safety Rule Book content here..."
                    value={safetyBook}
                    onChange={(e) => setSafetyBook(e.target.value)}
                    rows={15}
                    className="font-mono text-sm"
                    data-testid="safety-book-textarea"
                  />
                  <Button 
                    onClick={handleSaveSafetyBook}
                    disabled={savingBook}
                    className="w-full bg-[#0056D2] hover:bg-[#0044A4]"
                    data-testid="save-safety-book-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {savingBook ? 'Saving...' : 'Save Safety Rule Book'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Union Book */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-500" />
                  Union Book / Agreement
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-sm text-slate-500">
                    This content is used by Ask Stewie to answer union agreement questions.
                    Include article numbers and section references for accurate citations.
                  </p>
                  <Textarea
                    placeholder="Paste or type the Union Book content here..."
                    value={unionBook}
                    onChange={(e) => setUnionBook(e.target.value)}
                    rows={15}
                    className="font-mono text-sm"
                    data-testid="union-book-textarea"
                  />
                  <Button 
                    onClick={handleSaveUnionBook}
                    disabled={savingBook}
                    className="w-full bg-[#0056D2] hover:bg-[#0044A4]"
                    data-testid="save-union-book-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {savingBook ? 'Saving...' : 'Save Union Book'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Event Free Performance Tool Book Tab */}
        <TabsContent value="efp">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    Event Free Performance Tool Book
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Manage hazards and mitigation strategies used in safety briefings and job packets.
                  </CardDescription>
                </div>
                <Button 
                  onClick={() => handleOpenEfpDialog()}
                  className="bg-[#0056D2] hover:bg-[#0044A4]"
                  data-testid="add-efp-entry-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Hazard Entry
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {efpEntries.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed rounded-lg">
                  <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-500 mb-2">No hazard entries defined yet</p>
                  <p className="text-sm text-slate-400">Add hazard categories and mitigation strategies to enhance safety briefings</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {efpEntries.map((entry) => (
                    <div 
                      key={entry.id} 
                      className="border rounded-lg p-4 hover:border-[#0056D2] transition-colors"
                      data-testid={`efp-entry-${entry.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Badge variant="outline" className={getSeverityColor(entry.severity)}>
                              {entry.severity?.toUpperCase()}
                            </Badge>
                            <Badge variant="secondary">{entry.hazard_category}</Badge>
                            <h4 className="font-semibold text-lg">{entry.hazard_name}</h4>
                          </div>
                          <p className="text-slate-600 text-sm mb-3">{entry.hazard_description}</p>
                          
                          {/* Mitigation Strategies */}
                          <div className="mb-3">
                            <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Mitigation Strategies</p>
                            <ul className="list-disc list-inside text-sm text-slate-700 space-y-1">
                              {entry.mitigation_strategies?.map((strategy, idx) => (
                                <li key={idx}>{strategy}</li>
                              ))}
                            </ul>
                          </div>

                          {/* Work Types & PPE */}
                          <div className="flex flex-wrap gap-4 text-sm">
                            {entry.applicable_work_types?.length > 0 && (
                              <div>
                                <span className="text-xs font-semibold text-slate-500">Applies to: </span>
                                <span className="text-slate-600">
                                  {entry.applicable_work_types.join(', ')}
                                </span>
                              </div>
                            )}
                            {entry.ppe_required?.length > 0 && (
                              <div>
                                <span className="text-xs font-semibold text-slate-500">PPE Required: </span>
                                <span className="text-slate-600">
                                  {entry.ppe_required.join(', ')}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleOpenEfpDialog(entry)}
                            data-testid={`edit-efp-${entry.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteEfpEntry(entry.id)}
                            data-testid={`delete-efp-${entry.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* EFP Entry Dialog */}
          <Dialog open={efpDialogOpen} onOpenChange={setEfpDialogOpen}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700 text-white">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-white">
                  <ShieldCheck className="w-5 h-5 text-amber-400" />
                  {editingEfpEntry ? 'Edit Hazard Entry' : 'Add New Hazard Entry'}
                </DialogTitle>
                <DialogDescription className="text-slate-400">
                  Define hazard details and mitigation strategies for safety briefings.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Hazard Category */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="hazard_category" className="text-slate-300">Hazard Category *</Label>
                    <Select 
                      value={efpForm.hazard_category} 
                      onValueChange={(v) => setEfpForm(prev => ({ ...prev, hazard_category: v }))}
                    >
                      <SelectTrigger data-testid="efp-category-select" className="bg-slate-800 border-slate-600 text-white">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {hazardCategories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="severity" className="text-slate-300">Severity Level *</Label>
                    <Select 
                      value={efpForm.severity} 
                      onValueChange={(v) => setEfpForm(prev => ({ ...prev, severity: v }))}
                    >
                      <SelectTrigger data-testid="efp-severity-select" className="bg-slate-800 border-slate-600 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="critical">Critical</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Hazard Name */}
                <div>
                  <Label htmlFor="hazard_name" className="text-slate-300">Hazard Name *</Label>
                  <Input
                    id="hazard_name"
                    value={efpForm.hazard_name}
                    onChange={(e) => setEfpForm(prev => ({ ...prev, hazard_name: e.target.value }))}
                    placeholder="e.g., Arc Flash Exposure"
                    data-testid="efp-hazard-name"
                    className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                  />
                </div>

                {/* Hazard Description */}
                <div>
                  <Label htmlFor="hazard_description" className="text-slate-300">Hazard Description *</Label>
                  <Textarea
                    id="hazard_description"
                    value={efpForm.hazard_description}
                    onChange={(e) => setEfpForm(prev => ({ ...prev, hazard_description: e.target.value }))}
                    placeholder="Describe the hazard in detail..."
                    rows={3}
                    data-testid="efp-hazard-desc"
                    className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                  />
                </div>

                {/* Mitigation Strategies */}
                <div>
                  <Label className="flex items-center justify-between text-slate-300">
                    Mitigation Strategies *
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      onClick={handleAddMitigationStrategy}
                      className="border-slate-600 hover:bg-slate-700"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add Strategy
                    </Button>
                  </Label>
                  <div className="space-y-2 mt-2">
                    {efpForm.mitigation_strategies.map((strategy, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          value={strategy}
                          onChange={(e) => handleMitigationChange(index, e.target.value)}
                          placeholder={`Mitigation strategy ${index + 1}`}
                          data-testid={`efp-mitigation-${index}`}
                          className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                        />
                        {efpForm.mitigation_strategies.length > 1 && (
                          <Button 
                            type="button"
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleRemoveMitigationStrategy(index)}
                            className="text-red-400 hover:text-red-300 hover:bg-slate-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Applicable Work Types */}
                <div>
                  <Label className="text-slate-300">Applicable Work Types</Label>
                  <div className="flex flex-wrap gap-2 mt-2 p-3 border border-slate-600 rounded-md bg-slate-800">
                    {workTypes.map(type => (
                      <Badge 
                        key={type}
                        variant={efpForm.applicable_work_types.includes(type) ? "default" : "outline"}
                        className={`cursor-pointer transition-colors ${
                          efpForm.applicable_work_types.includes(type) 
                            ? 'bg-blue-600 hover:bg-blue-700' 
                            : 'border-slate-500 text-slate-300 hover:bg-slate-700'
                        }`}
                        onClick={() => toggleWorkType(type)}
                      >
                        {type}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* PPE Required */}
                <div>
                  <Label className="text-slate-300">PPE Required</Label>
                  <div className="flex flex-wrap gap-2 mt-2 p-3 border border-slate-600 rounded-md bg-slate-800">
                    {ppeOptions.map(ppe => (
                      <Badge 
                        key={ppe}
                        variant={efpForm.ppe_required.includes(ppe) ? "default" : "outline"}
                        className={`cursor-pointer transition-colors ${
                          efpForm.ppe_required.includes(ppe) 
                            ? 'bg-amber-500 hover:bg-amber-600' 
                            : 'border-slate-500 text-slate-300 hover:bg-slate-700'
                        }`}
                        onClick={() => togglePpe(ppe)}
                      >
                        {ppe}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Additional Notes */}
                <div>
                  <Label htmlFor="additional_notes" className="text-slate-300">Additional Notes</Label>
                  <Textarea
                    id="additional_notes"
                    value={efpForm.additional_notes}
                    onChange={(e) => setEfpForm(prev => ({ ...prev, additional_notes: e.target.value }))}
                    placeholder="Any additional safety notes or rule citations..."
                    rows={2}
                    data-testid="efp-notes"
                    className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setEfpDialogOpen(false)} className="border-slate-600 hover:bg-slate-700">
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveEfpEntry}
                  disabled={savingBook}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="save-efp-entry-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {savingBook ? 'Saving...' : (editingEfpEntry ? 'Update Entry' : 'Add Entry')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Audit Logs Tab */}
        <TabsContent value="audit">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Audit Logs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                {auditLogs.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No audit logs available</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {auditLogs.map((log) => (
                      <div 
                        key={log.id}
                        className="p-3 border rounded-lg text-sm"
                        data-testid={`audit-log-${log.id}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <Badge variant="outline">{log.action}</Badge>
                          <span className="text-xs text-slate-500">
                            {new Date(log.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-slate-600 font-mono text-xs">
                          {JSON.stringify(log.details)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPage;
